/* 
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import Styles from '../css/LoanInfoHeader.module.css';
import { useLoanContext } from '../contexts/LoanContext';
import UserProfileDropdown from './UserProfileDropdown';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import LoanInfoHeaderModal from './modals/LoanInfoHeaderModal';
import WarningModal from './modals/WarningModal';
import { InfoOutlined, ContentCopy, KeyboardArrowDown } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import useWindowDimensions from '../common/hooks/useWindowDimensions';
import {
  getTimestampAsDate,
  addMonthsYrsToTimestamp,
  daysBetween,
  getCurrentHourInTimeZone,
  getCurrentTimeInTimeZone,
  getObjProp,
  getServStatus,
  isStrEmptyOrNull,
} from '../common/UtilityFunctions';
import useBorrowerData from '../common/hooks/useBorrowerData';
import * as zipcodeToTimezone from 'zipcode-to-timezone';
import { STATE_COLLECTION_LAWS, STATE_LICENSING_REQUIREMENTS } from '../contracts/constants';
import axiosCall from '../services/axios';
import useAbortController from '../common/hooks/useAbortController';
import { useViewingContext } from '../contexts/ViewingContext';
import { useCommunicationContext } from '../contexts/CommunicationContext';
import useLoanServicing from '../common/hooks/useLoanServicing';
import { useBrwrContext } from '../contexts/BorrowerContext';
import useApplicationList from '../common/hooks/useApplicationList';
import { useLoginContext } from '../contexts/LoginContext';
import { useNavigationAlert } from '../contexts/NavigationAlertContext';
import { useLoanSearchQuery } from '../common/hooks/useLoanSearchQuery';

type EventDateData = {
  isLoading: boolean;
  dates: { [key: string]: string | undefined };
};

function LoanInfoHeader() {
  const { reset } = useAbortController();
  const { token } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const loanInfo = useLoanContext();
  const {
    disasterName,
    loanNumber,
    lpi,
    mailingState,
    propertyState,
    mailingZipCode,
    servicerLoan,
    servStatus,
    servStatusDescription,
    RCSServiced,
    loadingLoanData,
    setLoanNumber,
    primaryBorrowerNumber,
  } = loanInfo;
  const { loanServicing } = useLoanServicing();
  const { emailAddress: email } = useLoginContext();
  const { borrowerData } = useBrwrContext();
  const { applicationList } = useApplicationList('borrowerNumber,coBorrowerNumber');
  const [primaryBorrowerIndex, setPrimaryBorrowerIndex] = useState(-1);
  const [paidToDate, setPaidToDate] = useState('');
  const [loanStatus, setLoanStatus] = useState('');
  const [mgCommentModalOpen, setMGCommentModalOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const { commentList, numOutboundCalls, numContactMade, managerComments } = useCommunicationContext();
  const { loans } = useLoanSearchQuery({
    brwrEmail: isServicerView ? borrowerData[primaryBorrowerNumber]?.emailAddress : email,
    includeAllBorrowers: true,
    rcsServicedOnly: true,
  });
  const { handleNavigation } = useNavigationAlert();
  const COMPACT_HEADER_MAX_WIDTH = 1024;
  const { innerWidth } = useWindowDimensions();
  const isCompactHeader = innerWidth <= COMPACT_HEADER_MAX_WIDTH;
  const [headerExpanded, setHeaderExpanded] = useState(false);

  useEffect(() => {
    if (!isCompactHeader) {
      setHeaderExpanded(true);
    }
  }, [isCompactHeader]);

  const showHeaderToggle = isCompactHeader;
  const showHeaderDetails = !showHeaderToggle || headerExpanded;

  const toggleHeaderDetails = useCallback(() => {
    setHeaderExpanded((expanded) => !expanded);
  }, []);

  const displayLoanNumber = servicerLoan || loanNumber;
  const hasMultipleLoans = loans.length > 1;
  const loanSelectOptions = useMemo(
    () => (hasMultipleLoans ? loans : displayLoanNumber ? [{ loanNo: displayLoanNumber }] : []),
    [hasMultipleLoans, loans, displayLoanNumber],
  );

  const [callLimitReached, setCallLimitReached] = useState(false);
  const [showDisasterMessage, setShowDisasterMessage] = useState(false);
  const [naturalDisasterName, setNaturalDisasterName] = useState('');
  const [naturalDisasterDate, setNaturalDisasterDate] = useState('');

  // today's date
  const date = useMemo(() => {
    const today = new Date();

    return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
  }, []);

  const handleCopyLoanNumber = async () => {
    await navigator.clipboard.writeText(servicerLoan ? servicerLoan : loanNumber);
  };

  useEffect(() => {
    if (lpi) {
      setPaidToDate(getTimestampAsDate(lpi));
    }
  }, [lpi]);

  // Next payment is due one month after the last paid installment, the same
  // calculation used by getLpiDueDateString and Next Payment Due Information.
  const nextPaymentDueDate = useMemo(() => {
    const nextDue = addMonthsYrsToTimestamp(lpi, 1, 0);
    return nextDue > 0 ? getTimestampAsDate(nextDue) : '';
  }, [lpi]);

  type BorrowerDataWithNumber = (typeof borrowerData)['01'] & {
    borrowerNumber: string;
    productCode?: string;
    counselingComp?: string;
  };

  const [dynamicBorrowers, setDynamicBorrowers] = useState<BorrowerDataWithNumber[]>([]);
  const [initialDynamicBorrowers, setInitialDynamicBorrowers] = useState<BorrowerDataWithNumber[] | null>(null);

  // load borrower data from borrower list endpoint and application list endpoint
  useEffect(() => {
    const fetchBorrowersInApplicationOrder = async () => {
      try {
        const borrowerList = Object.values(borrowerData);
        const fullList: BorrowerDataWithNumber[] = borrowerList.map((b: any) => ({
          ...b,
          borrowerNumber: b.borrowerNumber,
        }));

        const borrowerByNumber: Record<string, BorrowerDataWithNumber> = {};
        fullList.forEach((b) => {
          borrowerByNumber[b.borrowerNumber] = b;
        });

        // order borrowers based on borrower number
        const orderedNumbers: string[] = [];
        applicationList?.forEach((app: any) => {
          const { borrowerNumber: main, coBorrowerNumber: co } = app;
          if (main && !orderedNumbers.includes(main)) orderedNumbers.push(main);
          if (co && co.trim() !== '' && !orderedNumbers.includes(co)) orderedNumbers.push(co);
        });

        const orderedBorrowers = orderedNumbers.map((num) => borrowerByNumber[num]).filter(Boolean);
        const leftoverBorrowers = fullList
          .filter((b) => !orderedNumbers.includes(b.borrowerNumber))
          .filter((b) => b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
          .map((b) => borrowerByNumber[b.borrowerNumber]);

        const finalBorrowerOrder = [...orderedBorrowers, ...leftoverBorrowers];

        // set the primary borrower to be the first borrower in the ordered list that is on the note and not removed
        setPrimaryBorrowerIndex(
          finalBorrowerOrder.findIndex((b) => b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED'),
        );
        setDynamicBorrowers(finalBorrowerOrder);
        if (!initialDynamicBorrowers) {
          setInitialDynamicBorrowers(finalBorrowerOrder);
        }
      } catch (error) {
        console.error('Error fetching borrowers:', error);
        setDynamicBorrowers([]);
      }
    };
    if (loanNumber && token) {
      fetchBorrowersInApplicationOrder();
    }
  }, [borrowerData, applicationList, loanNumber, token, reset]);

  useEffect(() => {
    var desc = getServStatus(loanInfo);
    setLoanStatus(desc);
  }, [loanInfo]);

  const stateCollectionNotices = useMemo(() => {
    const stateData = STATE_COLLECTION_LAWS[mailingState];
    return stateData || [];
  }, [mailingState]);

  const stateLicensingNotices = useMemo(() => {
    const stateData = STATE_LICENSING_REQUIREMENTS[propertyState];
    return stateData || [];
  }, [propertyState]);

  // Check the number of outbound calls and contacts made with the borrower
  // to confirm we have not reached either limit
  useEffect(() => {
    // do not call more than 30 times or contact more than 10 times in a week
    const outboundCallLimit = 30;
    const contactMadeLimit = 10;

    setCallLimitReached(numOutboundCalls >= outboundCallLimit || numContactMade >= contactMadeLimit);
  }, [commentList]);

  // Using the borrower's time zone, ensure the local time is not outside of 8AM - 9PM
  const getBorrowerDoNotCall = useCallback(() => {
    if (mailingZipCode) {
      const fiveDigitZipCode = mailingZipCode.length > 5 ? mailingZipCode.substring(0, 5) : mailingZipCode;
      const zone = zipcodeToTimezone.lookup(fiveDigitZipCode);
      const borrowerLocalHour = getCurrentHourInTimeZone(zone, false);
      // do not call before 8AM and after 9PM
      const morningLimit = 8;
      const nightLimit = 21;
      if (borrowerLocalHour !== -1) {
        const tooEarly = borrowerLocalHour < morningLimit;
        const tooLate = borrowerLocalHour >= nightLimit;

        if (tooEarly || tooLate) {
          return true;
        } else {
          return false;
        }
      }
    }
    return false;
  }, [mailingZipCode]);

  const getBorrowerLocalTime = useCallback(() => {
    if (mailingZipCode) {
      const fiveDigitZipCode = mailingZipCode.length > 5 ? mailingZipCode.substring(0, 5) : mailingZipCode;
      const zone = zipcodeToTimezone.lookup(fiveDigitZipCode);
      return getCurrentTimeInTimeZone(zone, false);
    }
    return '';
  }, [mailingZipCode]);

  const doNotCall = useMemo(() => getBorrowerDoNotCall(), [mailingZipCode]);
  const borrowerLocalTime = useMemo(() => getBorrowerLocalTime(), [mailingZipCode]);

  const [eventDateData, setEventDateData] = useState<EventDateData>({
    dates: {},
    isLoading: false,
  });
  const [initialEventDateData, setInitialEventDateData] = useState<EventDateData | null>(null);

  // Fetch event date data related to natural disasters
  const getEventDateData = useCallback(async () => {
    try {
      if (!loanNumber || !token) return;

      setEventDateData((prev) => ({ ...prev, isLoading: true }));
      const naturalDisasterEventDateCodes = ['S7A', 'S7B', 'MOE'];
      const requests = naturalDisasterEventDateCodes.map((eventCode) => {
        const request = {
          url: `live/loan/${loanNumber}/event/${eventCode}`,
          method: 'get',
        };
        const headers = {
          'Session-Token': token,
        };
        const config = reset();
        return axiosCall(request, config, headers)
          .then((response) => {
            console.log(response);
            return { eventCode, date: response.date };
          })
          .catch((error) => ({ eventCode, date: undefined }));
      });

      const responses = await Promise.all(requests);
      const dateResults: { [key: string]: string | undefined } = {};
      responses.forEach((response) => {
        dateResults[response.eventCode] = response.date;
      });

      setEventDateData({
        dates: dateResults,
        isLoading: false,
      });

      if (!initialEventDateData) {
        setInitialEventDateData({
          dates: dateResults,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error(`Error fetching event date data: ${error.message}`);
      setEventDateData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [loanNumber, token, reset]);

  useEffect(() => {
    getEventDateData();
  }, [getEventDateData]);

  useEffect(() => {
    if (loanNumber || (loanNumber && (doNotCall || callLimitReached || showDisasterMessage))) {
      setMGCommentModalOpen(true);
    }
  }, [loanNumber, doNotCall, callLimitReached, showDisasterMessage]);

  // handle logic for displaying natural disaster validation message
  useEffect(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(today.getDate() - 89);
    const disasterDate = eventDateData?.dates['S7B'] ? new Date(getTimestampAsDate(eventDateData.dates['S7B'])) : null;
    const moratoriumDate = eventDateData?.dates['MOE']
      ? new Date(getTimestampAsDate(eventDateData.dates['MOE']))
      : null;
    const disasterNotImpacted = eventDateData?.dates['S7A']
      ? new Date(getTimestampAsDate(eventDateData.dates['S7A']))
      : null;

    // if disaster date is within the last ninety days, but not in the future show validation message
    // OR
    // if disaster is more than 90 days ago, but moratorium date is in the future and disaster not
    // impacted date is null, show validation message
    if (
      disasterName !== null &&
      disasterDate !== null &&
      ((disasterDate >= ninetyDaysAgo && disasterDate <= today) ||
        (moratoriumDate !== null &&
          disasterDate < ninetyDaysAgo &&
          moratoriumDate > today &&
          disasterNotImpacted == null))
    ) {
      setShowDisasterMessage(true);
      setNaturalDisasterName(disasterName);
      setNaturalDisasterDate(disasterDate.toLocaleDateString('en-US'));
    } else {
      setShowDisasterMessage(false);
    }
  }, [eventDateData, disasterName]);

  return (
    <>
      {isServicerView &&
        (managerComments.length > 0 ||
          stateLicensingNotices.length > 0 ||
          stateCollectionNotices.length > 0 ||
          doNotCall ||
          callLimitReached ||
          showDisasterMessage) &&
        mgCommentModalOpen && (
          <LoanInfoHeaderModal
            open={mgCommentModalOpen}
            handleClose={() => setMGCommentModalOpen(false)}
            comments={managerComments}
            stateLicensingNotices={stateLicensingNotices}
            stateCollectionNotices={stateCollectionNotices}
            title=''
            doNotCall={doNotCall}
            borrowerLocalTime={borrowerLocalTime}
            callLimitReached={callLimitReached}
            naturalDisaster={showDisasterMessage}
            naturalDisasterName={naturalDisasterName}
            naturalDisasterDate={naturalDisasterDate}
          />
        )}
      {isServicerView && warningModalOpen && (
        <WarningModal
          open={warningModalOpen}
          handleClose={() => setWarningModalOpen(false)}
          warningMessages={['This loan is not active in Red Carpet. Do not post any entries.']}
        />
      )}
      <header className={Styles.header}>
        <section
          className={`${Styles.loanInfoData}${showHeaderToggle && !headerExpanded ? ` ${Styles.loanInfoDataCollapsed}` : ''}`}
        >
          {RCSServiced === false && isServicerView && loanNumber && !loadingLoanData && (
            <div className={Styles.headerItem}>
              <b className={Styles.headerTextRed}>DO NOT SERVICE</b>
              <Tooltip title='Warning Message'>
                <IconButton className={Styles.icon} onClick={() => setWarningModalOpen(true)}>
                  <InfoOutlined className={Styles.icon} />
                </IconButton>
              </Tooltip>
            </div>
          )}

          <div className={isCompactHeader ? Styles.loanHeaderMobile : undefined}>
            <div className={Styles.loanRow}>
              {loanSelectOptions.length > 0 ? (
                <div className={Styles.headerText}>
                  {'Loan: '}
                  <select
                    name='Loan'
                    value={loanNumber || displayLoanNumber}
                    className={Styles.loandropdown}
                    disabled={!hasMultipleLoans}
                    onChange={(e) => {
                      if (!hasMultipleLoans) return;
                      setLoanNumber(e.target.value);
                      handleNavigation('/loan-information');
                    }}
                  >
                    {loanSelectOptions.map((opt, j) => (
                      <option key={j} value={opt.loanNo}>
                        {opt.loanNo}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className={Styles.headerItem}>
                  {'Loan: '}
                  {displayLoanNumber}{' '}
                </div>
              )}
              {isCompactHeader && (servicerLoan !== '' || loanNumber !== '') && (
                <Tooltip title='Copy Loan'>
                  <IconButton className={Styles.copyButton} onClick={handleCopyLoanNumber}>
                    <ContentCopy className={Styles.icon} />
                  </IconButton>
                </Tooltip>
              )}
              {!isCompactHeader && (
                <div className={Styles.loanRowActions}>
                  <NavLink className={Styles.headerItem} to='/select-loans'>
                    <u className={Styles.headerText}>(select)</u>
                  </NavLink>
                  {(servicerLoan !== '' || loanNumber !== '') && (
                    <Tooltip title='Copy Loan'>
                      <IconButton className={Styles.copyButton} onClick={handleCopyLoanNumber}>
                        <ContentCopy className={Styles.icon} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
            {showHeaderToggle ? (
              <button
                type='button'
                className={Styles.loanDetailsToggle}
                onClick={toggleHeaderDetails}
                aria-expanded={headerExpanded}
                aria-label={headerExpanded ? 'Show less' : 'Show more'}
              >
                <span className={Styles.loanDetailsToggleLine} aria-hidden='true' />
                <span className={Styles.loanDetailsToggleLabel}>
                  {headerExpanded ? 'Show Less' : 'Show More'}
                  <KeyboardArrowDown
                    className={`${Styles.loanDetailsToggleIcon}${headerExpanded ? ` ${Styles.loanDetailsToggleIconExpanded}` : ''}`}
                  />
                </span>
                <span className={Styles.loanDetailsToggleLine} aria-hidden='true' />
              </button>
            ) : null}
          </div>
          {showHeaderDetails && (
            <>
              {isServicerView && (
                <div className={Styles.headerItem}>
                  Loan Type:&nbsp;
                  <div className={Styles.headerText}>{getObjProp(loanServicing, 'servicingLoanType')}&nbsp;</div>
                </div>
              )}
              <div className={Styles.headerItem}>
                Borrower:&nbsp;{' '}
                <div className={Styles.headerText}>
                  {(dynamicBorrowers &&
                    primaryBorrowerIndex >= 0 &&
                    dynamicBorrowers[primaryBorrowerIndex]?.fullName) ||
                    ''}
                  &nbsp;
                </div>
              </div>
              {isServicerView ? (
                <>
                  <div className={Styles.headerItem}>
                    Date:&nbsp; <div className={Styles.headerText}>{date}</div>
                  </div>
                  <div className={Styles.headerItem}>
                    Paid To:&nbsp; <div className={Styles.headerText}>{paidToDate}</div>
                  </div>
                  <div className={Styles.headerItem}>
                    Status:&nbsp;
                    <div className={servStatus == 'ABK' ? Styles.headerTextRed : Styles.headerText}>{loanStatus}</div>
                  </div>
                </>
              ) : (
                <div className={Styles.headerItem}>
                  Next Payment Due:&nbsp; <div className={Styles.headerText}>{nextPaymentDueDate}</div>
                </div>
              )}
              {isServicerView &&
                (managerComments.length > 0 ||
                  stateLicensingNotices.length > 0 ||
                  stateCollectionNotices.length > 0 ||
                  doNotCall ||
                  callLimitReached ||
                  showDisasterMessage) && (
                  <div className={Styles.headerItem}>
                    <Tooltip title='Info'>
                      <IconButton className={Styles.icon} onClick={() => setMGCommentModalOpen(true)}>
                        <InfoOutlined className={Styles.icon} />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
            </>
          )}
        </section>
        <section className={Styles.dropdown}>
          <UserProfileDropdown />
        </section>
      </header>
    </>
  );
}

export default LoanInfoHeader;
